import { useMemo } from 'react'
import { Box, Stack, Text, Loader, Group } from '@mantine/core'
import { MarkdownRenderer } from '../common/MarkdownRenderer'
import { PanelTitle } from '../common/PanelTitle'
import { StreamingCursor } from './StreamingCursor'
import { parseCharacterStream, parseResolutionStream, resolveCharacterName, stripSsml } from '../../lib/responseParser'
import { SpeakButton } from '../game2/SpeakButton'

// Render generated prose with any inline SSML expression tags stripped, so the
// screen shows clean text while speech uses the marked-up version.
function ProseRenderer({ content }) {
  return <MarkdownRenderer content={stripSsml(content || '')} />
}

// Build the per-segment speak descriptor, or null when speech context isn't
// available (e.g. legacy interface) so the button renders nothing.
function beatSegment(speakContext, segmentIndex) {
  if (!speakContext?.sessionId || !speakContext?.beatId) return null
  return {
    id: `beat:${speakContext.beatId}:${segmentIndex}`,
    kind: 'beat',
    sessionId: speakContext.sessionId,
    beatId: speakContext.beatId,
    segmentIndex,
  }
}

export function GeneratedContentPanel({ content, beatType, characters = [], speakContext = null }) {
  const isNarrativeResponse = beatType === 'narrative_response'
  const isCharacterResponse = beatType === 'character_response'
  const isResolution = beatType === 'resolution'

  // Parse streamed character text into blocks (memoized to avoid re-parsing every render)
  const parsedCharacters = useMemo(() => {
    if (!isCharacterResponse || !content?.data || typeof content.data !== 'string') return null
    return parseCharacterStream(content.data)
  }, [isCharacterResponse, content?.data])

  // Parse resolution stream into narrator + character segments
  const parsedResolution = useMemo(() => {
    if (!isResolution || !content?.data || typeof content.data !== 'string') return null
    return parseResolutionStream(content.data)
  }, [isResolution, content?.data])

  if (!content) return null

  const loadingLabel = isCharacterResponse
    ? 'Characters are responding...'
    : isResolution
      ? 'Crafting your story\'s conclusion...'
      : 'Generating narration...'

  // Show spinner while waiting for first streaming chunk
  if (content.loading && content.streaming && !content.data) {
    return (
      <Group gap="sm">
        <Loader size="xs" color="cyan" />
        <Text size="sm" c="dimmed" fs="italic">{loadingLabel}</Text>
      </Group>
    )
  }

  // Show spinner for non-streaming loading
  if (content.loading && !content.streaming) {
    return (
      <Group gap="sm">
        <Loader size="xs" color="cyan" />
        <Text size="sm" c="dimmed" fs="italic">{loadingLabel}</Text>
      </Group>
    )
  }

  // Streaming character_response — show each character as their block arrives
  if (isCharacterResponse && typeof content.data === 'string') {
    if (!parsedCharacters?.length) {
      if (content.streaming) {
        return (
          <Group gap="sm">
            <Loader size="xs" color="cyan" />
            <Text size="sm" c="dimmed" fs="italic">
              Characters are responding...
            </Text>
          </Group>
        )
      }
      // Fallback: delimiters didn't parse but content exists — show as markdown
      if (content.data) {
        return (
          <Box style={{ lineHeight: 1.5 }}>
            <ProseRenderer content={content.data} />
          </Box>
        )
      }
      return null
    }

    return (
      <Stack gap="md" style={{ lineHeight: 1.5 }}>
        {parsedCharacters.map((entry, idx) => (
          <Box key={entry.character_id || idx}>
            <PanelTitle c="cyan.4" mb={4}>
              {resolveCharacterName(entry, characters)}
            </PanelTitle>
            <ProseRenderer content={entry.response} />
            {content.streaming && idx === parsedCharacters.length - 1 && (
              <StreamingCursor />
            )}
          </Box>
        ))}
      </Stack>
    )
  }

  // Resolution — mixed narrator + character segments
  if (isResolution && typeof content.data === 'string') {
    if (!parsedResolution?.length) {
      if (content.streaming) {
        return (
          <Group gap="sm">
            <Loader size="xs" color="cyan" />
            <Text size="sm" c="dimmed" fs="italic">
              Crafting your story's conclusion...
            </Text>
          </Group>
        )
      }
      // Fallback: delimiters didn't parse but content exists — show as markdown
      if (content.data) {
        return (
          <Box style={{ lineHeight: 1.5 }}>
            <ProseRenderer content={content.data} />
          </Box>
        )
      }
      return null
    }

    return (
      <Stack gap="md" style={{ lineHeight: 1.5 }}>
        {parsedResolution.map((segment, idx) => {
          const isLastSegment = idx === parsedResolution.length - 1
          const cursor = content.streaming && isLastSegment ? <StreamingCursor /> : null

          return segment.type === 'narrator' ? (
            <Box key={idx} style={{ fontStyle: 'italic' }}>
              <ProseRenderer content={segment.text} />
              {cursor}
            </Box>
          ) : (
            <Box key={idx}>
              <PanelTitle c="cyan.4" mb={4}>
                {resolveCharacterName(segment, characters)}
              </PanelTitle>
              <ProseRenderer content={segment.text} />
              {cursor}
            </Box>
          )
        })}
      </Stack>
    )
  }

  // Streaming narrative — show text as it arrives with a cursor
  if (isNarrativeResponse && content.streaming) {
    if (!content.data) {
      return (
        <Group gap="sm">
          <Loader size="xs" color="cyan" />
          <Text size="sm" c="dimmed" fs="italic">
            Narration streaming...
          </Text>
        </Group>
      )
    }
    return (
      <Box style={{ lineHeight: 1.5 }}>
        <ProseRenderer content={content.data} />
        <StreamingCursor />
      </Box>
    )
  }

  if (!content.data) return null

  // character_response final — array of { character_name, response }
  if (isCharacterResponse && Array.isArray(content.data)) {
    return (
      <Stack gap="md" style={{ lineHeight: 1.5 }}>
        {content.data.map((entry, idx) => (
          <Box key={idx}>
            <Group gap={6} mb={4} wrap="nowrap">
              <PanelTitle c="cyan.4">
                {resolveCharacterName(entry, characters)}
              </PanelTitle>
              <SpeakButton segment={beatSegment(speakContext, idx)} />
            </Group>
            <ProseRenderer content={entry.response} />
          </Box>
        ))}
      </Stack>
    )
  }

  // narrative_response — show final text after streaming completes
  if (isNarrativeResponse) {
    return (
      <Box style={{ lineHeight: 1.5 }}>
        <Group gap={6} mb={4} wrap="nowrap" justify="flex-end">
          <SpeakButton segment={beatSegment(speakContext, 0)} />
        </Group>
        <ProseRenderer content={content.data} />
      </Box>
    )
  }

  // Fallback for unexpected data types (e.g. resolution/narrative HTTP returning non-string)
  const fallbackText = typeof content.data === 'string' ? content.data : ''
  if (!fallbackText) return null
  return <ProseRenderer content={fallbackText} />
}
